/**
 * The <mask> definitions for the silhouettes CSS cannot describe — letters, QR
 * modules, barcode bars, and any mask whose own effects change what it covers —
 * a blur, a shadow, an outline, less than full opacity. A `clip-path` is
 * all-or-nothing at one hard edge; those need a drawn, filtered silhouette that
 * the subject fades out through (see utils/elementMask's maskAlphaEffects).
 *
 * `clip-path` covers geometry and `mask-image: url(src)` covers a picture, but a
 * text layer used as a mask has to be re-drawn as SVG <text> (a <foreignObject>
 * inside a <mask> paints nothing in Chromium — probed), and a QR or a barcode has
 * to be drawn as the image it renders to.
 *
 * One <mask> per PAIR of layers, because a mask is written in the masked
 * element's own coordinates and two subjects of the same mask sit at different
 * offsets from it. The whole thing lives in a zero-sized <svg> beside the layers,
 * so the masks are in the same document as the fonts they need — which is exactly
 * why they cannot be data URLs.
 */
import { useEffect, useState } from 'react';
import {
    maskKind, maskDefId, isMaskElement, maskFor, maskAlphaEffects, usesSvgMask,
} from '../utils/elementMask';
import { textMaskLines } from '../utils/textMaskLines';
import { renderQrDataUrl } from '../utils/qrCode';
import { barcodeSvgDataUrl } from '../utils/barcodeImage';
import { shapePathD } from '../schema/shapeLibrary';
import { getCornerRadii } from '../panels/propertyControls';

/** The mask's box in the masked element's coordinates. */
function offsetOf(el, mask) {
    return {
        dx: (Number(mask.x) || 0) - (Number(el.x) || 0),
        dy: (Number(mask.y) || 0) - (Number(el.y) || 0),
        w: Math.max(1, Number(mask.width) || 1),
        h: Math.max(1, Number(mask.height) || 1),
    };
}

/** White paints, black hides — a luminance mask's alphabet. */
function TextMaskContent({ mask, dx, dy }) {
    const laid = textMaskLines(mask);
    if (!laid) return null;
    return (
        <g
            fill="#fff"
            style={{
                fontFamily: laid.fontFamily,
                fontSize: `${laid.fontSize}px`,
                fontWeight: laid.fontWeight,
                fontStyle: laid.fontStyle,
                letterSpacing: laid.letterSpacing ? `${laid.letterSpacing}px` : 'normal',
                whiteSpace: 'pre',
            }}
        >
            {laid.lines.map((line, i) => (
                <text key={i} x={dx + line.x} y={dy + line.y}>{line.text}</text>
            ))}
        </g>
    );
}

function QrMaskContent({ mask, dx, dy, w, h }) {
    const [url, setUrl] = useState('');
    const styles = mask.styles || {};
    useEffect(() => {
        let dead = false;
        renderQrDataUrl(mask, { size: 512 })
            .then((u) => { if (!dead) setUrl(u); })
            .catch(() => { if (!dead) setUrl(''); });
        return () => { dead = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mask.content, mask.src, styles.foreground, styles.background, styles.margin,
        styles.errorCorrection, styles.logoSize, styles.logoPadding, styles.logoRadius]);
    if (!url) return null;
    // Letterboxed exactly as the layer paints it (object-contain).
    return <image href={url} x={dx} y={dy} width={w} height={h} preserveAspectRatio="xMidYMid meet" />;
}

/**
 * A picture's own alpha. Stretched to the mask's box, which is what the CSS this
 * replaced did — a mask does not honour the crop or the fit of the layer it was
 * made from.
 */
function ImageMaskContent({ mask, dx, dy, w, h }) {
    if (!mask.src) return null;
    return <image href={mask.src} x={dx} y={dy} width={w} height={h} preserveAspectRatio="none" />;
}

function BarcodeMaskContent({ mask, dx, dy, w, h }) {
    const url = barcodeSvgDataUrl(mask);
    if (!url) return null;
    return <image href={url} x={dx} y={dy} width={w} height={h} preserveAspectRatio="none" />;
}

/**
 * Geometry — a star, an ellipse, a rounded box — drawn instead of clipped.
 *
 * Only reached when the mask carries an alpha effect: `clip-path` handles these
 * shapes crisply and cheaply, but it has exactly one hard edge, and a blur, a
 * shadow or a half-opaque fill is not one. The silhouette is filled white and the
 * filter above does the rest, so the masked layer fades out through the mask's
 * own effects (Figma's behaviour).
 */
function ShapeMaskContent({ mask, kind, dx, dy, w, h }) {
    if (kind === 'path') {
        const d = shapePathD(mask, w, h, dx, dy);
        return d ? <path d={d} fill="#fff" /> : null;
    }
    if (kind === 'ellipse') {
        return <ellipse cx={dx + w / 2} cy={dy + h / 2} rx={w / 2} ry={h / 2} fill="#fff" />;
    }
    const { tl, tr, br, bl } = getCornerRadii(mask.styles || {});
    const max = Math.min(w, h) / 2;
    const r = (n) => Math.min(Math.max(0, n), max);
    const [a, b, c, d] = [r(tl), r(tr), r(br), r(bl)];
    if (!a && !b && !c && !d) return <rect x={dx} y={dy} width={w} height={h} fill="#fff" />;
    const path = `M${dx + a},${dy}H${dx + w - b}A${b},${b} 0 0 1 ${dx + w},${dy + b}`
        + `V${dy + h - c}A${c},${c} 0 0 1 ${dx + w - c},${dy + h}`
        + `H${dx + d}A${d},${d} 0 0 1 ${dx},${dy + h - d}`
        + `V${dy + a}A${a},${a} 0 0 1 ${dx + a},${dy}Z`;
    return <path d={path} fill="#fff" />;
}

/**
 * The mask's own alpha effects as one filter chain: outline, shadows, Layer blur,
 * opacity — in the order a layer actually renders them.
 *
 * `feMorphology` is the outline and the shadow spread (a dilate is exactly what
 * either one does to a silhouette). A shadow's CSS blur RADIUS is σ = radius / 2,
 * while `filter: blur(N)` is σ = N — two different conventions the panel shows as
 * one word, converted here and identically in the export.
 */
function AlphaEffectsFilter({ id, fx, dx, dy, w, h, pad }) {
    const prims = [];
    let src = 'SourceGraphic';
    if (fx.grow > 0) {
        prims.push(<feMorphology key="grow" in="SourceGraphic" operator="dilate" radius={fx.grow} result="grown" />);
        src = 'grown';
    }
    // Every shadow, back to front — each one a dilated, blurred, offset, faded
    // copy of the silhouette. Only its ALPHA ever matters: the mask reads alpha.
    const names = [];
    fx.shadows.forEach((s, i) => {
        const from = s.spread > 0 ? `sd${i}` : src;
        if (s.spread > 0) {
            prims.push(<feMorphology key={`m${i}`} in={src} operator="dilate" radius={s.spread} result={`sd${i}`} />);
        } else if (s.spread < 0) {
            prims.push(<feMorphology key={`m${i}`} in={src} operator="erode" radius={-s.spread} result={`sd${i}`} />);
        }
        prims.push(
            <feGaussianBlur key={`b${i}`} in={s.spread ? `sd${i}` : from} stdDeviation={s.blur / 2} result={`sb${i}`} />,
        );
        prims.push(<feOffset key={`o${i}`} in={`sb${i}`} dx={s.dx} dy={s.dy} result={`so${i}`} />);
        prims.push(
            <feComponentTransfer key={`a${i}`} in={`so${i}`} result={`sh${i}`}>
                <feFuncA type="linear" slope={s.alpha} />
            </feComponentTransfer>,
        );
        names.push(`sh${i}`);
    });
    if (names.length) {
        prims.push(
            <feMerge key="merge">
                {names.map((n) => <feMergeNode key={n} in={n} />)}
                <feMergeNode in={src} />
            </feMerge>,
        );
    } else if (src !== 'SourceGraphic') {
        prims.push(<feMerge key="merge"><feMergeNode in={src} /></feMerge>);
    }
    // Layer blur softens the whole thing, shadows included — CSS `filter` blurs a
    // layer after its box-shadow is part of it.
    if (fx.blur > 0) prims.push(<feGaussianBlur key="blur" stdDeviation={fx.blur} />);
    if (fx.opacity < 1) {
        prims.push(
            <feComponentTransfer key="op">
                <feFuncA type="linear" slope={fx.opacity} />
            </feComponentTransfer>,
        );
    }
    return (
        <filter
            id={id}
            filterUnits="userSpaceOnUse"
            x={dx - pad}
            y={dy - pad}
            width={w + pad * 2}
            height={h + pad * 2}
        >
            {prims}
        </filter>
    );
}

function MaskDef({ el, mask }) {
    const kind = maskKind(mask);
    const { dx, dy, w, h } = offsetOf(el, mask);
    const ew = Math.max(1, Number(el.width) || 1);
    const eh = Math.max(1, Number(el.height) || 1);
    const fx = maskAlphaEffects(mask);
    const content = kind === 'image' ? <ImageMaskContent mask={mask} dx={dx} dy={dy} w={w} h={h} />
        : kind === 'text' ? <TextMaskContent mask={mask} dx={dx} dy={dy} />
            : kind === 'qr' ? <QrMaskContent mask={mask} dx={dx} dy={dy} w={w} h={h} />
                : kind === 'barcode' ? <BarcodeMaskContent mask={mask} dx={dx} dy={dy} w={w} h={h} />
                    // Geometry, drawn rather than clipped — only when an effect
                    // means a clip can no longer say what this mask covers.
                    : <ShapeMaskContent mask={mask} kind={kind} dx={dx} dy={dy} w={w} h={h} />;
    // Nothing to cut with — an empty text layer, a barcode whose value this
    // symbology cannot encode, a QR still rendering. Fall back to the mask's BOX,
    // which is what every other mask kind does and what the export falls back to
    // as well. Cutting to nothing would make the subject vanish with no way for
    // the user to tell why.
    const raw = content || <rect x={dx} y={dy} width={w} height={h} fill="#fff" />;
    // The mask's own effects, applied to its silhouette. The filter region is the
    // mask's box grown by everything that reaches past it — a shadow's offset and
    // blur, an outline, 3σ of Layer blur (where a Gaussian is spent) — or the soft
    // edge would be clipped back into a hard one, the very thing being fixed.
    const filterId = `${maskDefId(mask.id, el.id)}-fx`;
    const pad = fx.bleed + 4;
    const body = fx.any ? <g filter={`url(#${filterId})`}>{raw}</g> : raw;
    const effectsFilter = fx.any ? (
        <AlphaEffectsFilter id={filterId} fx={fx} dx={dx} dy={dy} w={w} h={h} pad={pad} />
    ) : null;
    return (
        <>
            {effectsFilter}
            <mask
                id={maskDefId(mask.id, el.id)}
                maskUnits="userSpaceOnUse"
                x={0}
                y={0}
                width={ew}
                height={eh}
                // ALPHA, not the SVG default of luminance: what is being matched
                // is Figma's alpha mask, and CSS mask-image behaved that way too.
                // Under luminance a black cut-out PNG would hide everything it
                // covers — precisely backwards.
                style={{ maskType: 'alpha' }}
            >
                {body}
            </mask>
        </>
    );
}

/**
 * Every pair in this frame that needs a drawn silhouette. Renders nothing at all
 * when there are none, which is the usual case.
 */
export default function MaskDefs({ elements = [] }) {
    const pairs = [];
    elements.forEach((el) => {
        if (isMaskElement(el) || !el.maskId) return;
        const mask = maskFor(el, elements);
        if (mask && usesSvgMask(mask)) pairs.push({ el, mask });
    });
    if (!pairs.length) return null;
    return (
        <svg
            width="0"
            height="0"
            aria-hidden="true"
            className="absolute pointer-events-none"
            style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }}
        >
            <defs>
                {pairs.map(({ el, mask }) => (
                    <MaskDef key={`${mask.id}-${el.id}`} el={el} mask={mask} />
                ))}
            </defs>
        </svg>
    );
}
